import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MbaIcon } from '../../../assignment/atoms/mba-icon/mba-icon';
import {
  PLATFORM_LABEL,
  PLATFORM_LOGO,
  ago,
  channelLabel,
  compact,
  metricLabel,
  percent,
  relativeDay,
  shortDay,
} from '../../hybrid-reporting.helpers';
import { HybridNotPosted, METRIC_KEYS, MetricKey, ReportMetric, ReportRow, ReportSort, ReportTotals } from '../../hybrid-reporting.types';

type NumCol = Exclude<ReportSort, 'posted'>;
const NUM_COLS: { key: NumCol; label: string }[] = [
  { key: 'views', label: 'Views' },
  { key: 'engagements', label: 'Engagements' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
  { key: 'er', label: 'Eng. rate' },
];

@Component({
  selector: 'hr-posts-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MbaIcon],
  templateUrl: './hr-posts-table.html',
})
export class HrPostsTable {
  readonly rows = input.required<ReportRow[]>();
  readonly totals = input.required<ReportTotals>();
  readonly totalsLabel = input.required<string>();
  readonly sort = input.required<ReportSort>();
  readonly hot = input.required<ReportMetric>();
  readonly notPosted = input<HybridNotPosted[]>([]);
  readonly creatorNames = input.required<Record<string, string>>();
  readonly currentId = input<string | null>(null);
  readonly statsLoading = input(false);
  readonly now = input.required<Date>();
  /** applicationId → ISO time Nudge is next available, for creators nudged in this session. */
  readonly nudgedUntil = input<Record<string, string>>({});
  readonly nudging = input<string | null>(null);
  readonly filtered = input(false);

  readonly open = output<string>();
  readonly sortBy = output<ReportSort>();
  readonly nudge = output<HybridNotPosted>();
  readonly clearFilters = output<void>();
  readonly copyAdCode = output<string>();

  protected readonly cols = NUM_COLS;
  protected readonly logos = PLATFORM_LOGO;
  protected readonly platformLabel = PLATFORM_LABEL;
  protected readonly skeletonCells = [1, 2, 3, 4, 5, 6, 7];

  protected readonly colSpan = 12;
  protected readonly channelName = channelLabel;

  protected readonly sortLabel = computed(() => metricLabel(this.sort()));

  protected value(row: ReportRow, col: NumCol): string {
    return col === 'er' ? percent(row.er) : compact(row[col]);
  }

  protected total(col: NumCol): string {
    const t = this.totals();
    return col === 'er' ? percent(t.er) : compact(t[col]);
  }

  protected posted(row: ReportRow): string {
    return relativeDay(row.postedAt, this.now());
  }

  protected refreshed(row: ReportRow): string {
    return `Refreshed ${ago(row.stats?.refreshedAt, this.now())}`;
  }

  protected sourceTitle(row: ReportRow): string {
    const s = row.stats;
    if (!s?.source) return 'No stats from Ultra Social or Super Social yet';
    const fromSuper = METRIC_KEYS.filter((k: MetricKey) => s.sources[k] === 'super');
    if (s.source === 'ultra') {
      return fromSuper.length
        ? `Ultra Social. Super Social filled: ${fromSuper.join(', ')}`
        : 'All stats from Ultra Social';
    }
    return `Super Social: Ultra Social had no ${fromSuper.join(', ')} for this post`;
  }

  protected rowLabel(row: ReportRow): string {
    const name = row.creator?.name ?? 'Creator';
    return `${name}, ${row.post.deliverableLabel} on ${channelLabel(row.post.socialChannel)}, ${compact(row.views)} views. Open details`;
  }

  protected notPostedStatus(np: HybridNotPosted): string {
    return np.videoApproved ? 'Video approved, not posted yet' : 'Waiting for the creator to post';
  }

  protected nudgeBlockedUntil(np: HybridNotPosted): string | null {
    const until = this.nudgedUntil()[np.applicationId] ?? np.nudgeAvailableAt;
    if (!until || new Date(until).getTime() <= this.now().getTime()) return null;
    return `Nudged · again ${relativeDay(until, this.now()) === 'Today' ? 'later today' : shortDay(until)}`;
  }

  protected onCopyAdCode(event: MouseEvent, code: string): void {
    // The row opens the drawer on click; copying shouldn't.
    event.stopPropagation();
    this.copyAdCode.emit(code);
  }

  protected creatorName(id: string): string {
    return this.creatorNames()[id] ?? 'Creator';
  }

  protected onRowKey(event: KeyboardEvent, id: string): void {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open.emit(id);
    }
  }
}
